import type { Data } from '@strapi/types';
import type { FunctionComponent, JSX } from 'react';

import { AbbFile } from '@/assets/icons/components/abb-file';
import { AbbMoreDots } from '@/assets/icons/components/abb-more-dots';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';
import { DataTable } from '@/ui-components/data-table/data-table';
import { Dropdown } from '@/ui-components/dropdown/dropdown';
import type { DropdownItemProps } from '@/ui-components/dropdown/types';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';
import { Paragraph } from '@/ui-components/paragraph/paragraph';
import { useProjectFilesTableParams } from './hooks/use-project-files-table-params';

export const ProjectFilesTable: FunctionComponent<{
  isUploadingPending?: boolean;
  projectFiles?: Data.Entity<'api::project-file.project-file'>[];
}> = ({ isUploadingPending, projectFiles }) => {
  const { t, isPending, getIconNameAndColor, format, downloadBom, downloadAttachment } = useProjectFilesTableParams();
  const { addSearchParam } = useNavigation();
  const { isDarkTheme } = useTheme();

  const getMenuItems = (projectFile: Data.Entity<'api::project-file.project-file'>, isBom?: boolean | null) => {
    const items = [] as DropdownItemProps[];

    if (isBom) {
      items.push(
        {
          value: 'DownloadXLSX',
          label: <span className='text-sm w-full text-left px-4 py-3'>{t('T_DOWNLOAD_AS_XLSX')}</span>,
          onClick: () => downloadBom({ file: projectFile, type: 'xlsx' })
        },
        {
          value: 'DownloadPDC',
          label: <span className='text-sm w-full text-left px-4 py-3'>{t('T_DOWNLOAD_AS_PDC')}</span>,
          onClick: () => downloadBom({ file: projectFile, type: 'pdc' })
        }
      );
    }

    items.push(
      {
        value: 'Download',
        label: <span className='text-sm w-full text-left px-4 py-3'>{isBom ? t('T_DOWNLOAD_AS_TXT') : t('T_DOWNLOAD')}</span>,
        onClick: () => downloadAttachment(projectFile)
      },
      {
        value: 'Delete',
        label: <span className='!text-brand text-sm w-full text-left px-4 py-3'>{t('T_DELETE')}</span>,
        onClick: () =>
          addSearchParam([
            { key: 'modal', value: 'Alert' },
            { key: 'id', value: projectFile.documentId },
            { key: 'type', value: 'projectFile' }
          ])
      }
    );
    return items;
  };

  const renderMenuCell = (projectFile: Data.Entity<'api::project-file.project-file'>, isBom?: boolean | null) => (
    <Dropdown
      label={<AbbMoreDots size={16} fill={isDarkTheme ? '#f1f1f1' : '#1f1f1f'} className='cursor-pointer mr-1' />}
      inline
      isMenu
      arrowIcon={false}
      menuItems={getMenuItems(projectFile, isBom)}
    />
  );

  const renderNameCell = ({ name, iconText, color }: { name: string; iconText: string; color: string }) => (
    <Flexbox className='gap-2 w-full'>
      <AbbFile isDarkTheme={isDarkTheme} backgroundTextColor={color} value={iconText} size={44} className='min-w-11' />
      <Paragraph variant='text-sm' className='self-center'>
        {name}
      </Paragraph>
    </Flexbox>
  );

  const tableData = projectFiles?.reduce(
    (acc, cur) => {
      const { name, color } = getIconNameAndColor(cur.title ?? '');
      acc.push({
        name: renderNameCell({
          name: cur.title?.replace('T_BOM', t('T_BOM')) ?? '',
          iconText: name?.substring(0, 4) ?? '',
          color: color ?? ''
        }),
        date: format(cur?.updatedAt, 'DD/MM/YYYY'),
        tool: cur.projectDocument?.documentType?.title ? (t(cur.projectDocument?.documentType?.title) ?? '') : '',
        menu: renderMenuCell(cur, cur.isBom)
      });
      return acc;
    },
    [] as {
      name: JSX.Element;
      date: string;
      tool: string;
      menu: JSX.Element;
    }[]
  );

  const getColumns = () => {
    if (!tableData) return [];
    return Object.keys(tableData[0]);
  };

  if (isPending || isUploadingPending) {
    return (
      <Flexbox justify='center' className='mt-5'>
        <LoadingIndicator sizing='large' color='primary' />
      </Flexbox>
    );
  }

  return (
    <Flexbox direction='col' className='w-full h-full flex justify-start relative overflow-y-scroll scrollbar-rounded'>
      {tableData?.length ? (
        <DataTable<(typeof tableData)[0]> columns={getColumns()} data={tableData ?? []} withMenuCell />
      ) : (
        <Paragraph variant='text-sm-semibold' className='text-center pt-2'>
          {t('T_NO_DOCUMENTS')}
        </Paragraph>
      )}
    </Flexbox>
  );
};
